import {trpc} from '@/api/trpcClient';
import {useStoreUnsafe} from '@/store';
import {useAuth0} from '@auth0/auth0-react';
import {ErrorCode, isFormattedTrpcError} from '@zentact/common';
import {useEffect} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {useUrlToken} from '../../hooks/useUrlToken';

export const SignUpPage = () => {
  const {loginWithRedirect, isAuthenticated, user, logout} = useAuth0();

  const navigate = useNavigate();
  // Not clearing url here, because we want to keep invitation parameters for signup redirect
  const {token, clearSession} = useUrlToken({clearUrl: false});
  const [searchParams] = useSearchParams();
  const {tenant} = useStoreUnsafe();

  const formDefaults = trpc.merchantRegistration.step1FormDefaults.useQuery(
    {
      // biome-ignore lint/style/noNonNullAssertion: TODO
      registrationToken: token!,
    },
    {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: 'always',
      staleTime: Number.POSITIVE_INFINITY,
      cacheTime: Number.POSITIVE_INFINITY,
      enabled: !!token,
      onError: error => {
        const errorCode =
          isFormattedTrpcError(error) &&
          error.data.errorCode === ErrorCode.MERCHANT_REGISTRATION_NOT_FOUND_OR_SESSION_EXPIRED
            ? error.data.errorCode
            : ErrorCode.ERROR_GENERIC;
        navigate('/error', {
          state: {errorCode},
        });
      },
    }
  );

  const associateUser = trpc.merchantRegistration.step1AssociateRegistrationWithUser.useMutation({
    onSuccess: () => {
      const registrationUrl = `/registration/${token}`;
      clearSession();
      navigate(registrationUrl);
    },
    onError: () => {
      navigate('/error', {state: {errorCode: 'MERCHANT_REGISTRATION_NOT_FOUND_FOR_USER'}});
    },
  });

  useEffect(() => {
    if (isAuthenticated || !formDefaults.data) return;

    const invitation = searchParams.get('invitation');
    const organization = searchParams.get('organization');
    const organizationName = searchParams.get('organization_name');
    if (
      invitation &&
      organization &&
      organizationName &&
      // Only redirect to signup if registration was not already accepted
      formDefaults.data.status === 'INITIALIZED'
    ) {
      loginWithRedirect({
        authorizationParams: {
          invitation,
          organization,
          // biome-ignore lint/style/useNamingConvention: Auth0 uses snake case
          organization_name: organizationName,
          // biome-ignore lint/style/useNamingConvention: Auth0 uses snake case
          ...(formDefaults.data.email && {login_hint: formDefaults.data.email}),
        },
        appState: {returnTo: '/signup'},
      });
    } else {
      // if there is no invitation, user is following an email link for a registration that was already accepted
      loginWithRedirect({
        appState: {returnTo: '/signup'},
        authorizationParams: {
          // biome-ignore lint/style/useNamingConvention: Auth0 uses snake case
          ...(formDefaults.data.email && {login_hint: formDefaults.data.email}),
        },
      });
    }
  }, [isAuthenticated, token, formDefaults.data]);

  useEffect(() => {
    // if user is not logged in or form defaults are not loaded yet
    if (!user?.email || !formDefaults.data) return;

    // When user is logged in, but email is different from the one in invitation, we logout current user and redirect to signup page
    if (user.email.toLowerCase() !== formDefaults.data.email?.toLowerCase()) {
      if (tenant?.logoutUri) {
        window.location.href = tenant.logoutUri;
      } else {
        logout({
          logoutParams: {returnTo: `${window.location.origin}/signup${window.location.search}`},
        });
      }
      return;
    }

    if (!token) {
      navigate('/error', {
        state: {errorCode: ErrorCode.MERCHANT_REGISTRATION_NOT_FOUND_OR_SESSION_EXPIRED},
      });
      return;
    }

    // if registration is initialized, we can go ahead and associate current user with it
    if (formDefaults.data.status === 'INITIALIZED') {
      associateUser.mutate({
        registrationToken: token,
      });
      return;
    }

    clearSession();
    navigate(`/registration/${token}`);
  }, [user, formDefaults.data]);

  return (
    <div className="flex items-center justify-center h-screen">
      <div>
        <svg
          aria-hidden="true"
          className="inline w-8 h-8 mr-2 text-gray-200 fill-primary-600 animate-spin dark:text-gray-600"
          viewBox="0 0 100 101"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
            fill="currentColor"
          />
          <path
            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
            fill="currentFill"
          />
        </svg>
        <span className="sr-only">Loading...</span>
      </div>
    </div>
  );
};
